import { useEffect, useState } from 'react';
import {
  Button,
  CloseButton,
  Dialog,
  Field,
  NativeSelect,
  Portal,
  Text,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';
import { datadogRum } from '@datadog/browser-rum';
import api from '@services/api';
import ErrorLabel from '@components/ErrorLabel';
import Loading from '@components/Loading';
import CustomDialogBackdrop from '@components/CustomDialogBackdrop';

export type StageOption = {
  id: string;
  name: string;
};

interface Props {
  funnelId: string;
  funnelName: string;
  stages: StageOption[];
  onClose: () => void;
  onDeleted: () => void;
}

const DeleteStageDialog = ({ funnelId, funnelName, stages, onClose, onDeleted }: Props) => {
  const [loadingCount, setLoadingCount] = useState(true);
  const [customerCount, setCustomerCount] = useState(0);
  const [targetId, setTargetId] = useState('');
  const [targetError, setTargetError] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const response = await api.get(`/sales_funnel/${funnelId}/customers/count`);
        if (active) setCustomerCount(response.data?.count ?? 0);
      } catch (error) {
        if (active) toast.error('Erro ao verificar clientes da etapa.');
        datadogRum.addError(new Error('Fail to count customers in stage'), { error, funnelId });
      } finally {
        if (active) setLoadingCount(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [funnelId]);

  const hasCustomers = customerCount > 0;
  const otherStages = stages.filter((s) => s.id !== funnelId);
  const cannotMove = hasCustomers && otherStages.length === 0;

  const handleDelete = async () => {
    if (hasCustomers && !targetId) {
      setTargetError('Selecione a etapa de destino.');
      return;
    }
    setTargetError('');
    setDeleting(true);
    try {
      await api.delete(`/sales_funnel/${funnelId}`, {
        params: hasCustomers ? { moveToFunnelId: targetId } : undefined,
      });
      toast.success('Etapa excluída!');
      onDeleted();
    } catch (error) {
      toast.error('Erro ao excluir etapa. Tente novamente.');
      datadogRum.addError(new Error('Fail to delete sales funnel stage'), { error, funnelId });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog.Root
      size="md"
      placement="center"
      open
      onOpenChange={onClose}
      closeOnInteractOutside={false}
    >
      <Portal>
        <CustomDialogBackdrop />
        <Dialog.Positioner>
          <Dialog.Content bg="grayscale.fg">
            <Dialog.Header>
              <Dialog.Title>Excluir etapa &quot;{funnelName}&quot;?</Dialog.Title>
            </Dialog.Header>

            <Dialog.Body display="flex" flexDirection="column" gap={3}>
              {loadingCount ? (
                <Loading />
              ) : (
                <>
                  {hasCustomers ? (
                    <>
                      <Text color="fg.muted">
                        Esta etapa possui {customerCount} cliente(s). Selecione a etapa para
                        onde eles serão movidos antes da exclusão.
                      </Text>

                      {cannotMove ? (
                        <ErrorLabel text="Não há outra etapa disponível para mover os clientes. Crie uma etapa antes de excluir esta." />
                      ) : (
                        <Field.Root invalid={!!targetError}>
                          <Field.Label>Mover clientes para</Field.Label>
                          <NativeSelect.Root size="sm" variant="subtle">
                            <NativeSelect.Field
                              placeholder="Selecione a etapa de destino"
                              value={targetId}
                              onChange={(e) => {
                                setTargetId(e.currentTarget.value);
                                setTargetError('');
                              }}
                            >
                              {otherStages.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.name}
                                </option>
                              ))}
                            </NativeSelect.Field>
                            <NativeSelect.Indicator />
                          </NativeSelect.Root>
                          {targetError && <ErrorLabel text={targetError} />}
                        </Field.Root>
                      )}
                    </>
                  ) : (
                    <Text color="fg.muted">
                      Esta etapa não possui clientes. Esta ação não pode ser desfeita.
                    </Text>
                  )}
                </>
              )}
            </Dialog.Body>

            <Dialog.Footer>
              <Dialog.ActionTrigger asChild>
                <Button variant="outline" disabled={deleting}>
                  Cancelar
                </Button>
              </Dialog.ActionTrigger>
              <Button
                bg="red.500"
                color="white"
                onClick={handleDelete}
                loading={deleting}
                disabled={loadingCount || cannotMove}
              >
                Excluir
              </Button>
            </Dialog.Footer>

            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default DeleteStageDialog;
